/* eslint-disable react/no-unused-prop-types */
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled, { ThemeProvider } from 'styled-components';
import useToggle from 'hooks/useToggle';
import { HeaderProvider } from './headerContext';
import MobilePane from './MobilePane';
import HeaderMobile from './HeaderMobile';
import HeaderDefault from './HeaderDefault';
import defaultTheme, { inverse as inverseTheme } from '../../themes';

const StyledHeader = styled.div`
  /** TODO: PE-10744 **/
  .inverse {
    #book-bike-input,
    #header-book-bike,
    #header-mobile-book-bike,
    #book-bike-button-no-studios,
    #header-mobile-book-bike-button-no-studios {
      ${(props) => props.theme.borders.borderWhite1};
      background-color: transparent;
      color: ${(props) => props.theme.colors.grayscale.white};

      :hover,
      :active {
        background-color: ${(props) => props.theme.colors.grayscale.gray2};
        color: ${(props) => props.theme.colors.grayscale.black};
      }

      &[aria-label='close menu'] {
        background-color: ${(props) => props.theme.colors.grayscale.gray2};
        color: ${(props) => props.theme.colors.grayscale.black};
      }
    }
  }
`;

/**
 * SCS responsive header component.
 */
function Header(props) {
  const {
    currentRegion: currentRegionProp,
    onRegionChange: onRegionChangeProp,
    themeMode,
    LinkWrapper,
    showBookBikeButton,
  } = props;
  const [isOpen, toggle] = useToggle(false);
  const theme = themeMode === 'inverse' ? inverseTheme : defaultTheme;
  const [currentRegion, setCurrentRegion] = useState(currentRegionProp);
  const showSecondaryButton = showBookBikeButton;
  const onRegionChange = useCallback(
    (region) => {
      setCurrentRegion(region);
      onRegionChangeProp(region);
    },
    [onRegionChangeProp]
  );

  // update currentRegion when currentRegionProp changes
  useEffect(() => {
    if (currentRegionProp.id !== currentRegion.id) {
      setCurrentRegion(currentRegionProp);
    }
  }, [currentRegionProp]);

  return (
    <StyledHeader>
      <ThemeProvider theme={theme}>
        <>
          <HeaderDefault
            id="header"
            {...props}
            currentRegion={currentRegion}
            onRegionChange={onRegionChange}
          />
          <HeaderMobile
            isOpen={isOpen}
            menuButtonClick={toggle}
            className={themeMode}
            LinkWrapper={LinkWrapper}
            showSecondaryButton={showSecondaryButton}
          />
        </>
      </ThemeProvider>
      <HeaderProvider value={{ ...props, currentRegion, onRegionChange }}>
        <MobilePane
          isOpen={isOpen}
          menuButtonClick={toggle}
          showBookBikeButton={showBookBikeButton}
        />
      </HeaderProvider>
    </StyledHeader>
  );
}

/**
 * propTypes for this component
 */
Header.propTypes = {
  /** The current active pathname of the url */
  currentPath: PropTypes.string,
  /** The currently active region */
  currentRegion: PropTypes.shape({
    id: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
  }).isRequired,
  /**
   *  Nav items array
   */
  navItems: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      url: PropTypes.string,
    })
  ).isRequired,
  /**
   * Click handler for the logo
   */
  onLogoClick: PropTypes.func,
  /**
   * Click handler for the nav items
   */
  onNavItemClick: PropTypes.func,
  /**
   * onChange handler for Book A Bike studios items
   */
  onStudioSelect: PropTypes.func,
  /**
   * Click handler for the subnav items
   */
  onSubNavItemClick: PropTypes.func,
  /**
   *  Regions object of arrays
   */
  regions: PropTypes.objectOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        title: PropTypes.string.isRequired,
      })
    )
  ).isRequired,
  /**
   * Function to execute when region is changed
   */
  onRegionChange: PropTypes.func,
  /**
   * Function to execute when region menu button is toggled
   */
  onRegionToggle: PropTypes.func,
  /**
   * Collection of props for the user badge component.
   */
  userBadgeProps: PropTypes.shape({
    /**
     * List of URLs to fill the UserBadge drop-down.
     */
    urlList: PropTypes.arrayOf(
      PropTypes.shape({
        text: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
      })
    ),
    /**
     *  User object
     */
    user: PropTypes.shape({
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      id: PropTypes.string,
    }),
    /**
     *  Fires when user selects a different item in the user badge.
     */
    onChange: PropTypes.func,
    /**
     *  Fires when user click on an item within the user badge.
     */
    onClick: PropTypes.func,
  }),
  /**
   * Number of series in Series Count
   */
  seriesCount: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  /**
   * Boolean indicating if Series Count should be displayed
   */
  showSeriesCount: PropTypes.bool,
  /**
   * List of all studios.
   */
  studios: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      title: PropTypes.string.isRequired,
    })
  ).isRequired,
  /**
   * Wrapper for Next.js Link or React Router Link
   */
  LinkWrapper: PropTypes.func,
  /**
   * Wrapper for Next.js Link or React Router Link
   */
  themeMode: PropTypes.string,
  /**
   * Show the "Book a Bike" button or not from bookBike.js
   */
  showBookBikeButton: PropTypes.bool,
};

/**
 * Default propTypes
 */
Header.defaultProps = {
  userBadgeProps: {
    user: null,
    urlList: [],
  },
  onLogoClick: null,
  onRegionChange: () => {},
  onRegionToggle: () => {},
  onNavItemClick: null,
  onStudioSelect: null,
  onSubNavItemClick: null,
  showSeriesCount: false,
  LinkWrapper: null,
  currentPath: typeof window !== 'undefined' ? window.location.pathname : '/',
  themeMode: '',
  showBookBikeButton: true,
};

/* @component */
export default Header;
